import { PortalReadState } from '../portal-read-state.tsx';
import { WorkPage, WorkSection } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { partnerApi, PartnerApiError } from '../../api/partner.ts';
import { usePartnerElevation, isFreshAuthRequired, isMfaSetupRequired } from '../../components/partner-elevation.tsx';
import { partnerMayAct, usePartnerIdentity, PARTNER_SESSION_QUERY_KEY } from '../../components/partner-layout.tsx';
import { Alert, Button, LoadingBlock, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';

export default function PartnerAccountPage() {
  useDocumentTitle('Partner account');
  const identity = usePartnerIdentity();
  const queryClient = useQueryClient();
  const elevation = usePartnerElevation();

  const [slug, setSlug] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [slugSaved, setSlugSaved] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [payoutError, setPayoutError] = useState<string | null>(null);
  const [payoutSaved, setPayoutSaved] = useState(false);

  const account = useQuery({
    queryKey: ['partner', 'account'],
    queryFn: partnerApi.account,
  });

  // Seeded from the server rather than kept in sync with it: the field is a
  // draft the moment somebody types in it, and re-seeding on every refetch
  // would overwrite what they were writing.
  const currentSlug = account.data?.slug;
  useEffect(() => {
    if (currentSlug) setSlug(currentSlug);
  }, [currentSlug]);

  // The server's own rule, from the shared set — see `partnerMayAct`.
  const active = partnerMayAct(identity.data?.partner.status);
  // With the deployment's master switch off there is no factor to enrol and no
  // elevation to pass, so `assertPartnerFreshAuth` refuses this change for
  // everybody, permanently. A live form in front of a gate nothing can open is
  // just a slower way of reading the notice below it.
  const payoutChangeable = identity.data ? identity.data.mfa.enabled : true;

  const saveSlug = useMutation({
    mutationFn: () => partnerApi.updateSlug(slug),
    onSuccess: () => {
      setSlugError(null);
      setSlugSaved(true);
      void account.refetch();
      // The header and the overview both show the link, and both read the
      // session payload.
      void queryClient.invalidateQueries({ queryKey: PARTNER_SESSION_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['partner', 'dashboard'] });
    },
    onError: (caught) => {
      setSlugSaved(false);
      setSlugError(caught instanceof PartnerApiError ? caught.message : 'That link could not be saved.');
    },
  });

  const savePayout = useMutation({
    mutationFn: () => partnerApi.updatePayoutAccount(paypalEmail),
    onSuccess: () => {
      setPayoutError(null);
      setPayoutSaved(true);
      setPaypalEmail('');
      void account.refetch();
      void queryClient.invalidateQueries({ queryKey: ['partner', 'dashboard'] });
    },
    onError: (caught) => {
      setPayoutSaved(false);
      // Two different problems behind one 403: a proof that has gone stale,
      // which a dialog fixes, and an account with nothing to prove with, which
      // only enrollment fixes.
      if (isFreshAuthRequired(caught)) {
        setPayoutError(null);
        elevation.begin(() => savePayout.mutate());
        return;
      }
      if (isMfaSetupRequired(caught)) {
        // The advice has to match the reason. Pointing somebody at Security
        // when the deployment has switched two-factor off sends them to a page
        // with nothing on it — and contradicts the notice above this form.
        setPayoutError(
          payoutChangeable
            ? 'Changing where your money is sent needs a second sign-in code. Add an authenticator app or a security key under Security first.'
            : 'A second sign-in code is turned off on this site, so this cannot be changed here. Contact the partner team.',
        );
        return;
      }
      setPayoutError(caught instanceof PartnerApiError ? caught.message : 'That payout address could not be saved.');
    },
  });

  if (account.isLoading) return <PortalReadState title="Account" label="Loading your account…" />;
  if (account.isError || !account.data) {
    return (
      <PortalReadState
        title="Account"
        error={account.error instanceof Error ? account.error.message : 'Your account could not be loaded.'}
        retry={() => account.refetch()}
      />
    );
  }
  const data = account.data;

  function submitSlug(event: FormEvent) {
    event.preventDefault();
    saveSlug.mutate();
  }

  function submitPayout(event: FormEvent) {
    event.preventDefault();
    savePayout.mutate();
  }

  return (
    <div className="portal-page">
      <WorkPage title="Account" description="Your partner details, your referral link and where your money goes.">
        {elevation.panel ? <div className="mb-4">{elevation.panel}</div> : null}

        <WorkSection title="Details">
          <dl className="mt-2 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-[color:var(--color-muted)]">Partner name</dt>
              <dd className="font-medium">{data.displayName}</dd>
            </div>
            <div>
              <dt className="text-[color:var(--color-muted)]">Contact</dt>
              <dd className="font-medium">
                {data.contactName} · {data.contactEmail}
              </dd>
            </div>
            <div>
              <dt className="text-[color:var(--color-muted)]">Status</dt>
              <dd className="font-medium">{data.status.replace(/_/g, ' ')}</dd>
            </div>
            <div>
              <dt className="text-[color:var(--color-muted)]">Tax documentation</dt>
              <dd className="font-medium">{data.taxDocumentationOnFile ? 'On file' : 'Not received'}</dd>
            </div>
          </dl>
          <p className="mt-3 text-sm text-[color:var(--color-muted)]">
            To change your partner name or contact details, reply to any message from the partner team.
          </p>
        </WorkSection>

        <WorkSection title="Referral link">
          <p className="mt-1 break-all font-mono text-sm">{data.url}</p>
          {/* The old link is retired rather than deleted, so anything already
            printed keeps working and no historical commission moves. */}
          <p className="mt-1 text-sm text-[color:var(--color-muted)]">
            Your old link keeps working after a change, and nothing you have already earned moves.
          </p>
          {slugError ? (
            <div className="mt-3">
              <Alert tone="error">{slugError}</Alert>
            </div>
          ) : null}
          {slugSaved ? (
            <div className="mt-3">
              <Alert tone="success">Your referral link has been updated.</Alert>
            </div>
          ) : null}
          <form className="mt-3" onSubmit={submitSlug} noValidate>
            <TextInput
              label="Link name"
              value={slug}
              onChange={(event) => {
                setSlug(event.target.value);
                setSlugSaved(false);
              }}
              hint="Lower-case letters, numbers and hyphens."
            />
            <Button
              type="submit"
              variant="primary"
              busy={saveSlug.isPending}
              disabled={!active || slug === data.slug || slug.length === 0}
            >
              Save link
            </Button>
            {!active ? (
              <p className="mt-2 text-sm text-[color:var(--color-muted)]">
                Paused while your partner account is not active.
              </p>
            ) : null}
          </form>
        </WorkSection>

        <WorkSection title="Payout address">
          <p className="mt-1 text-sm text-[color:var(--color-muted)]">
            {data.paypalEmailMasked
              ? `Payouts are sent to ${data.paypalEmailMasked}.`
              : 'No PayPal address is on file, so nothing can be sent yet.'}
          </p>
          {/* Stated up front rather than discovered on submit: this is the one
            action a partner with no second factor genuinely cannot perform. */}
          {identity.data && !identity.data.mfa.enrolled ? (
            <div className="mt-3">
              {identity.data.mfa.enabled ? (
                <Alert tone="info" title="This one change needs a second sign-in code">
                  Everything else in the portal works on your password alone. Because a stolen session could redirect
                  your money, changing the payout address asks for a factor you prove on the spot —{' '}
                  <Link className="font-semibold text-[color:var(--color-brand)] underline" to="/partner/security">
                    add one under Security
                  </Link>
                  .
                </Alert>
              ) : (
                /* With the master switch off there is nothing to enrol and the
                 freshness gate can never be passed, so pointing at Security
                 would send somebody to a page offering no methods. */
                <Alert tone="warning" title="The payout address cannot be changed here">
                  A second sign-in code is turned off on this site, and changing where your money is sent needs one.
                  Contact the partner team and they will update it for you.
                </Alert>
              )}
            </div>
          ) : null}
          {payoutError ? (
            <div className="mt-3">
              <Alert tone="error">{payoutError}</Alert>
            </div>
          ) : null}
          {payoutSaved ? (
            <div className="mt-3">
              <Alert tone="success">Your payout address has been updated.</Alert>
            </div>
          ) : null}
          <form className="mt-3" onSubmit={submitPayout} noValidate>
            <TextInput
              label="PayPal email"
              type="email"
              autoComplete="off"
              disabled={!payoutChangeable}
              value={paypalEmail}
              onChange={(event) => {
                setPaypalEmail(event.target.value);
                setPayoutSaved(false);
              }}
            />
            <Button
              type="submit"
              variant="primary"
              busy={savePayout.isPending}
              disabled={!active || !paypalEmail || !payoutChangeable}
            >
              Save payout address
            </Button>
          </form>
        </WorkSection>
      </WorkPage>
    </div>
  );
}
